/* ═══════════════════════════════════════════════════════════════
   Prefs — per-USER UI preferences synced to the server (user_prefs
   table), so table layouts follow the user across devices and
   desktop/web. localStorage stays as the fast first-paint cache;
   the server copy wins once loaded.
   ═══════════════════════════════════════════════════════════════ */
import * as cache from './cloudCache';
import type { TableOpts } from './cloudCache';

const prefOpts: TableOpts = { jsonFields: ['value'] };

interface PrefRow {
  id: string;
  userId: string;
  key: string;
  value: any;
}

let userId: string | null = null;
let map = new Map<string, any>();
let ready = false;
const subs = new Set<() => void>();
const timers = new Map<string, ReturnType<typeof setTimeout>>();

export const Prefs = {
  isReady: (): boolean => ready,

  /** Load all prefs for the signed-in user. Call once after auth. */
  load: async (uid: string): Promise<void> => {
    userId = uid;
    try {
      const rows = await cache.query<PrefRow>('user_prefs', { userId: uid }, prefOpts);
      map = new Map(rows.map((r) => [r.key, r.value]));
    } catch (e) {
      console.warn('[prefs] load failed (falling back to local-only):', e);
    }
    ready = true;
    subs.forEach((f) => f());
  },

  /** Sign-out / firm-switch: drop everything. */
  reset: (): void => {
    userId = null;
    map = new Map();
    ready = false;
  },

  get: (key: string): any => map.get(key),

  /** Re-pull prefs from the server (bypassing the cache) so cross-machine
   *  changes arrive without a re-login. Keys with a pending debounced save
   *  keep their local value — a refetch never clobbers unsaved edits. */
  refetch: async (): Promise<void> => {
    if (!userId) return;
    try {
      cache.invalidate('user_prefs');
      const rows = await cache.query<PrefRow>('user_prefs', { userId }, prefOpts);
      const next = new Map(rows.map((r) => [r.key, r.value]));
      for (const key of timers.keys()) {
        if (map.has(key)) next.set(key, map.get(key));
      }
      map = next;
      subs.forEach((f) => f());
    } catch (e) {
      console.warn('[prefs] refetch failed:', e);
    }
  },

  /** Set + debounce-save to the server (fire-and-forget). */
  set: (key: string, value: any): void => {
    map.set(key, value);
    if (!userId) return;
    const uid = userId;
    const existing = timers.get(key);
    if (existing) clearTimeout(existing);
    timers.set(
      key,
      setTimeout(() => {
        // the timer has fired — the key no longer has a pending save, so
        // refetch() must stop pinning it to the local value (leaving it in
        // `timers` silently disabled cross-machine sync for the whole session)
        timers.delete(key);
        const safe = key.replace(/[^A-Za-z0-9_.-]/g, '_');
        cache
          .save('user_prefs', { id: `up_${uid}_${safe}`, userId: uid, key, value }, prefOpts)
          .catch((e) => console.warn('[prefs] save failed:', e));
      }, 800)
    );
  },

  subscribe: (fn: () => void): (() => void) => {
    subs.add(fn);
    return () => {
      subs.delete(fn);
    };
  },
};
